import { curveLinear, line } from 'd3-shape';
import { toInteger } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import {
    EmptyPointImage,
    fetchVectorText,
    getVectorTextFromCache,
    parseColorToRgba,
    parseStrokeDasharray,
} from '@/utils/file-kit';
import { getLayerInnerStyle, getLayerWrapStyle } from '@/utils/layer-kit';
import { calculateNewCoordinates, getPointsBetweenAngle } from '@/utils/math-kit';
import { renderPointStyle, renderSvgChild } from '@/utils/render-kit';
import {
    ILayerProps,
    IMarkupLayerProps,
    IRenderLayerEvent,
    MarkupPointType,
    MarkupTextPositionType,
    MarkupType,
} from '@/version-types';
import LabelKit from '../utils/label-kit';
import { getPointImage, MARKUP_POINT_SIZE } from '../utils/layer';

const MarkupImage: React.FC<{ layer: ILayerProps; normalIcon?: boolean } & IRenderLayerEvent> = ({
    layer,
    // 是否为设计师编辑器图层icon
    normalIcon = false,
    renderEnd,
}) => {
    const { opacity, width, height } = layer;
    const {
        markupType,
        label,
        fill,
        anchor,
        hasLine,
        startPoint,
        endPoint,
        centerOffset,
        imageTransform: { flipH, flipV },
    } = layer.data as IMarkupLayerProps;

    const originImgWidth = toInteger(width);
    const originImgHeight = toInteger(height);
    const offsetX = 0;
    const offsetY = 0;
    const wrapStyle = getLayerWrapStyle(opacity);
    const innerStyle = getLayerInnerStyle(originImgWidth, originImgHeight, offsetX, offsetY, 1, flipH, flipV);

    const [_, setRefreshRender] = useState<number>(0);
    const isArrow = markupType === MarkupType.Arrow;
    const isSegment = markupType === MarkupType.Segment;
    const drawPoint = isArrow || isSegment;

    // # begin 获取端点
    const startPointText = getVectorTextFromCache(startPoint.url);
    const hasStartPointText = !!startPointText;
    const endPointText = getVectorTextFromCache(endPoint.url);
    const hasEndPointText = !!endPointText;

    const startPointImg = useMemo(() => {
        if (!drawPoint || !hasStartPointText) return EmptyPointImage;
        const text = getVectorTextFromCache(startPoint.url);
        return getPointImage(text, startPoint, fill);
    }, [drawPoint, hasStartPointText, startPoint.url, fill.width, fill.color]);

    const endPointImg = useMemo(() => {
        if (!drawPoint || !hasEndPointText) return EmptyPointImage;
        const text = getVectorTextFromCache(endPoint.url);
        return getPointImage(text, endPoint, fill);
    }, [drawPoint, hasEndPointText, endPoint.url, fill.width, fill.color]);
    // # end 获取端点

    // # begin 加载远程资源
    useEffect(() => {
        const fetchList: any[] = [];
        if (startPoint.url && !hasStartPointText) {
            fetchList.push(fetchVectorText(startPoint.url));
        }
        if (endPoint.url && !hasEndPointText) {
            fetchList.push(fetchVectorText(endPoint.url));
        }
        if (fetchList.length) {
            Promise.all(fetchList).then(() => {
                setRefreshRender(new Date().getTime());
            });
        }
    }, [startPoint.url, hasStartPointText, endPoint.url, hasEndPointText]);
    // # end 加载远程资源

    useEffect(() => {
        if (renderEnd) {
            setTimeout(() => {
                renderEnd?.();
            }, 0);
        }
    }, [renderEnd]);

    const dataDrawId = { ...(!normalIcon && { 'data-draw-id': `${layer.id}` }) };
    const fillColorRgba = parseColorToRgba(fill.color);
    const svgColor = `rgb(${fillColorRgba.r},${fillColorRgba.g},${fillColorRgba.b})`;
    let startPointFixOffset = 0;
    let endPointFixOffset = 0;
    const fix = (20 / 100) * fill.width;
    if (startPoint.type === MarkupPointType.StraightArrow) {
        startPointFixOffset = fix;
    }
    if (endPoint.type === MarkupPointType.StraightArrow) {
        endPointFixOffset = fix;
    }

    if (markupType === MarkupType.Arrow) {
        if (startPoint.type === MarkupPointType.SolidCircle) {
            startPointFixOffset = 1;
            endPointFixOffset = 1;
        }
    }

    // 获取图形样式
    const getGraphicWrapStyle = () => {
        return {
            position: 'absolute',
            left: centerOffset.x,
            top: centerOffset.y,
            right: 0,
            height: 0,
        } as React.CSSProperties;
    };

    const labelRenderInfo = LabelKit.getLabelRenderInfoByLayer(layer);
    // 定位样式
    const labelPosition: any = {
        left: '50%',
        padding: `${labelRenderInfo.paddingV}px ${labelRenderInfo.paddingH}px`,
    };
    let rotateStyle = '';
    if (label.rotate) {
        rotateStyle = ` rotate(${label.rotate}deg)`;
    }
    const renderText = LabelKit.getRenderText(label);

    /**
     * 渲染箭头标注
     **/
    const renderArrow = () => {
        const drawOffset = hasLine ? fill.width : 0;
        const halfFillWidth = fill.width / 2;
        const startPos = {
            x: drawOffset + (startPoint.offset / MARKUP_POINT_SIZE) * fill.width + startPointFixOffset,
            y: halfFillWidth,
        };
        const endPos = {
            x: width - drawOffset - (endPoint.offset / MARKUP_POINT_SIZE) * fill.width - endPointFixOffset,
            y: halfFillWidth,
        };
        let pathD = `M${startPos.x},${startPos.y} L${endPos.x},${endPos.y}`;

        if (label.position === MarkupTextPositionType.Center) {
            // 绘制文字镂空
            if (renderText) {
                const labelBoxWidth = labelRenderInfo.renderArea.width;
                const halfLineWidth = Math.ceil((width - labelBoxWidth) / 2);
                const drawXEnd = Math.max(halfLineWidth - halfFillWidth, startPos.x);
                const drawXStart = Math.min(halfLineWidth + halfFillWidth + labelBoxWidth, endPos.x);
                pathD = `M${startPos.x},${startPos.y} L${drawXEnd},${endPos.y} M${drawXStart},${endPos.y} L${endPos.x},${endPos.y}`;
            }
            // 文字剧中
            labelPosition.top = '50%';
            labelPosition.transform = `translate(-50%,-50%)${rotateStyle}`;
        } else if (label.position === MarkupTextPositionType.Down) {
            // 居下样式
            labelPosition.top = `${labelRenderInfo.renderPosition}px`;
            labelPosition.transform = `translateX(-50%)${rotateStyle}`;
        } else if (label.position === MarkupTextPositionType.Up) {
            // 居上样式
            labelPosition.bottom = `${labelRenderInfo.renderPosition}px`;
            labelPosition.transform = `translateX(-50%)${rotateStyle}`;
        }

        // 竖线标签
        const getLineRect = (arrowAnchor: number[], style) => {
            return renderSvgChild(
                'rect',
                {
                    stroke: 'none',
                    x: 0,
                    y: 0,
                    width: fill.width,
                    height: Math.max(0, Math.max(arrowAnchor?.[0], 0) - arrowAnchor?.[1]),
                },
                style,
            );
        };

        return (
            <div {...dataDrawId} style={{ ...getGraphicWrapStyle() }}>
                {/* 文案 */}
                {renderText ? (
                    <div
                        style={{
                            ...LabelKit.getRenderStyle(layer),
                            ...labelPosition,
                        }}
                    >
                        {`${renderText}`}
                    </div>
                ) : null}
                {/* svg 图形 */}
                <svg
                    viewBox={`0 0 ${width} ${fill.width}`}
                    xmlns="http://www.w3.org/2000/svg"
                    version="1.1"
                    style={{
                        position: 'absolute',
                        zIndex: 0,
                        overflow: 'visible',
                        transform: 'translateY(-50%)',
                        opacity: fillColorRgba.a,
                    }}
                    stroke={svgColor}
                    fill={svgColor}
                >
                    {/* 左侧竖线 */}
                    {hasLine ? (
                        <>
                            {getLineRect(anchor.arrowLeft, {
                                transform: `translate(0px,${-anchor.arrowLeft?.[0] + fill.width / 2}px)`,
                            })}
                        </>
                    ) : null}
                    {/* 左侧箭头 */}
                    {renderPointStyle(startPointImg, {
                        transform: `translate(${drawOffset + startPointFixOffset}px,-${
                            (startPointImg.height - fill.width) / 2
                        }px)`,
                    })}
                    {/* 中间横线 */}
                    {pathD ? (
                        <path
                            d={pathD}
                            strokeWidth={fill.width}
                            strokeDasharray={parseStrokeDasharray(fill.dasharray, fill.width) as string}
                            strokeLinecap={fill.linecap}
                        />
                    ) : null}
                    {/* 右侧箭头 */}
                    {renderPointStyle(endPointImg, {
                        transform: `translate(${width - drawOffset - endPointFixOffset}px,-${
                            (endPointImg.height - fill.width) / 2
                        }px) rotateY(180deg)`,
                    })}
                    {/* 右侧竖线 */}
                    {hasLine ? (
                        <>
                            {getLineRect(anchor.arrowRight, {
                                transform: `translate(${width - fill.width}px,${
                                    -anchor.arrowRight?.[0] + fill.width / 2
                                }px)`,
                            })}
                        </>
                    ) : null}
                </svg>
            </div>
        );
    };

    /**
     * 渲染2段标注
     **/
    const renderSegment = () => {
        const hasStartPoint = !!startPointImg.element.tag;
        const hadEndPoint = !!endPointImg.element.tag && anchor.points![1];
        const startPointInfo = {
            deg: 0,
            point: anchor.points![0],
            scale: 1,
        };
        if (hasStartPoint) {
            startPointInfo.deg =
                -getPointsBetweenAngle(anchor.points![0], {
                    x: 1,
                    y: 0,
                }) + 180;
            startPointInfo.point = calculateNewCoordinates(
                anchor.points![0],
                startPointInfo.deg,
                -(startPoint.offset / MARKUP_POINT_SIZE) * fill.width * startPointInfo.scale,
            );
        }
        const endPointInfo = {
            deg: 0,
            point: anchor.points![1],
            scale: 0,
        };
        if (hadEndPoint) {
            endPointInfo.scale = startPointInfo.scale;
            endPointInfo.deg = -getPointsBetweenAngle(anchor.points![1], {
                x: 1,
                y: 0,
            });
            endPointInfo.point = calculateNewCoordinates(
                anchor.points![1],
                endPointInfo.deg,
                (endPoint.offset / MARKUP_POINT_SIZE) * fill.width * endPointInfo.scale,
            );
        }

        const drawPoints = [startPointInfo.point, { x: 0, y: 0 }];
        if (endPointInfo.point) {
            drawPoints.push(endPointInfo.point);
        }

        const offsetX = -Math.min(...drawPoints.map((p) => p.x));
        const offsetY = -Math.min(...drawPoints.map((p) => p.y));
        const newPoints = drawPoints.map((point) => ({
            x: point.x + offsetX,
            y: point.y + offsetY,
        }));

        const minX = Math.min(...newPoints.map((p) => p.x));
        const maxX = Math.max(...newPoints.map((p) => p.x));
        const minY = Math.min(...newPoints.map((p) => p.y));
        const maxY = Math.max(...newPoints.map((p) => p.y));
        const boxWidth = maxX - minX;
        const boxHeight = maxY - minY;

        const pathD = line()
            .curve(curveLinear)
            .x((d: any) => d.x)
            .y((d: any) => d.y)(newPoints);

        if (label.position === MarkupTextPositionType.Center) {
            // 文字剧中
            labelPosition.transform = `translate(-50%,-50%)${rotateStyle}`;
            if (anchor.points![1]?.x < 0) {
                labelPosition.left = `-${labelRenderInfo.renderWidth / 2 + labelRenderInfo.marginH}px`;
            } else {
                labelPosition.left = `${labelRenderInfo.renderWidth / 2 + labelRenderInfo.marginH}px`;
            }
        } else if (label.position === MarkupTextPositionType.Down) {
            // 居下样式
            labelPosition.top = `${labelRenderInfo.renderPosition}px`;
            labelPosition.transform = `translate(-50%,0)${rotateStyle}`;
        } else if (label.position === MarkupTextPositionType.Up) {
            // 居上样式
            labelPosition.bottom = `${labelRenderInfo.renderPosition}px`;
            labelPosition.transform = `translate(-50%,0)${rotateStyle}`;
        }

        return (
            <div {...dataDrawId} style={{ ...getGraphicWrapStyle(), right: 'auto' }}>
                {renderText ? (
                    <div
                        style={{
                            position: 'absolute',
                            zIndex: 1,
                            left: anchor.points![1]?.x || 0,
                            top: anchor.points![1]?.y || 0,
                        }}
                    >
                        <div
                            style={{
                                ...LabelKit.getRenderStyle(layer),
                                ...labelPosition,
                            }}
                        >
                            {`${renderText}`}
                        </div>
                    </div>
                ) : null}
                <div
                    style={{
                        width: boxWidth,
                        height: boxHeight,
                        transform: `translate(${-newPoints[1].x}px,${-newPoints[1].y}px)`,
                    }}
                >
                    <svg
                        viewBox={`0 0 ${boxWidth} ${boxHeight}`}
                        width={boxWidth}
                        height={boxHeight}
                        preserveAspectRatio="none"
                        xmlns="http://www.w3.org/2000/svg"
                        version="1.1"
                        style={{
                            zIndex: 0,
                            overflow: 'visible',
                            opacity: fillColorRgba.a,
                        }}
                        stroke={svgColor}
                        fill={svgColor}
                    >
                        {/* 左侧箭头 */}
                        {hasStartPoint ? (
                            <g
                                style={{
                                    transform: `translate(${anchor.points![0].x + offsetX}px,${
                                        anchor.points![0].y + offsetY
                                    }px) rotate(${startPointInfo.deg}deg) scale(${startPointInfo.scale})`,
                                }}
                            >
                                {renderPointStyle(startPointImg, {
                                    transform: `translate(-${fill.width / 2}px,-${startPointImg.height / 2}px)`,
                                })}
                            </g>
                        ) : null}
                        <path
                            d={pathD}
                            fill="none"
                            strokeWidth={Math.max(fill.width * startPointInfo.scale, 1)}
                            strokeDasharray={parseStrokeDasharray(fill.dasharray, fill.width) as string}
                            strokeLinecap={fill.linecap}
                            strokeLinejoin="round"
                        />
                        {/* 右侧箭头 */}
                        {hadEndPoint ? (
                            <g
                                style={{
                                    transform: `translate(${anchor.points![1].x + offsetX}px,${
                                        anchor.points![1].y + offsetY
                                    }px) rotate(${endPointInfo.deg}deg) scale(${endPointInfo.scale})`,
                                }}
                            >
                                {renderPointStyle(endPointImg, {
                                    transform: `translate(${fill.width / 2}px,-${
                                        endPointImg.height / 2
                                    }px) rotateY(180deg)`,
                                })}
                            </g>
                        ) : null}
                    </svg>
                </div>
            </div>
        );
    };

    return (
        <div style={wrapStyle}>
            <div style={{ ...innerStyle }}>
                {isArrow ? <>{renderArrow()}</> : null}
                {isSegment ? <>{renderSegment()}</> : null}
            </div>
        </div>
    );
};
export default React.memo(MarkupImage);
